import { type MouseEvent as ReactMouseEvent } from 'react';
import { useIntl } from 'react-intl';

import {
  CrosshairIcon,
  PanHorizontalIcon,
  ZoomToSelectionIcon,
} from '@dynatrace/strato-icons';

import { DownloadDataButton } from './DownloadDataButton.js';
import { ExploreButton } from './ExploreButton.js';
import { PanButton } from './PanButton.js';
import { ResetButton } from './ResetButton.js';
import { ZoomInOutButtons } from './ZoomInOutButtons.js';
import { ZoomModeButton } from './ZoomModeButton.js';
import type { InteractionMode } from '../../../core/components/interactions-overlay/types.js';
import {
  ChartToolbarRenderer,
  type MenuDisplayMode,
} from '../../../core/components/toolbar/ChartToolbarRenderer.js';
import type { ToolbarPlacement } from '../../../core/types/toolbar.js';
import { getA11yToolbarConfig } from '../../../core/utils/zoom.js';
import { defaultToolbar } from '../../constants/defaults.js';
import { zIndicesXYChartAreas } from '../../constants/xy-chart-z-index-areas.js';
import { useToolbar } from '../../hooks/useToolbar.js';
import { useXYChartData } from '../../hooks/useXYChartData.js';
import { XYCHART_MESSAGES } from '../../messages/messages.js';
import {
  useInteractionModeState,
  useToolbarState,
} from '../../store/selectors.js';
import { buildAndDownloadCsv } from '../../utils/build-and-download-csv.js';

type XYChartToolbarProps = {
  onMouseEnter?: (event?: ReactMouseEvent<HTMLDivElement, MouseEvent>) => void;
  onMouseLeave?: (react?: ReactMouseEvent<HTMLDivElement, MouseEvent>) => void;
};

const getMenuIcon = (
  interactionMode: InteractionMode,
  isZoomEnabled: boolean | undefined,
) => {
  switch (interactionMode) {
    case 'pan':
      return <PanHorizontalIcon />;
    case 'zoom':
      return isZoomEnabled ? <ZoomToSelectionIcon /> : <CrosshairIcon />;
    default:
      return <CrosshairIcon />;
  }
};

export const XYChartToolbar = ({
  onMouseEnter,
  onMouseLeave,
}: XYChartToolbarProps) => {
  const { visible } = useToolbarState();
  const { collapsed, draggable, placement, hidden, intents } = useToolbar();
  const interactionMode = useInteractionModeState();
  const { axesMetadata } = useXYChartData();
  const intl = useIntl();

  const isZoomEnabled = axesMetadata.isZoomEnabled;
  const isPanEnabled = axesMetadata.isPanEnabled;
  const exploreBtnPlace = isZoomEnabled || isPanEnabled ? 'menu' : 'auto';
  const isDownloadEnabled = true;

  const initialA11yState = getA11yToolbarConfig({
    zoom: { enabled: isZoomEnabled },
    pan: { enabled: isPanEnabled },
    download: { enabled: isDownloadEnabled },
    intent: !!intents,
  });

  const menuIcon = getMenuIcon(interactionMode, isZoomEnabled);

  const menuLabel =
    !isPanEnabled && !isZoomEnabled
      ? intl.formatMessage(XYCHART_MESSAGES.zoomModeExplore)
      : undefined;

  let menuDisplayMode: MenuDisplayMode;

  if (isPanEnabled || isZoomEnabled) {
    if (isZoomEnabled || isDownloadEnabled) {
      menuDisplayMode = 'always';
    } else {
      menuDisplayMode = 'expanded';
    }
  } else {
    if (isDownloadEnabled) {
      menuDisplayMode = 'collapsed';
    } else {
      menuDisplayMode = 'never';
    }
  }

  const { seriesWithDatapoints } = useXYChartData();

  /** Handles the download button when clicked. */
  const handleDownloadDataToolbar = () =>
    buildAndDownloadCsv(seriesWithDatapoints);

  const showDefaultToolbarDownloadButton = isDownloadEnabled && !intents;

  return !hidden ? (
    <ChartToolbarRenderer
      visible={visible}
      placement={placement ?? (defaultToolbar.placement as ToolbarPlacement)}
      draggable={draggable}
      collapsed={collapsed}
      initialA11yState={initialA11yState}
      zIndex={zIndicesXYChartAreas.toolbar}
      onMouseEnter={onMouseEnter}
      onMouseLeave={onMouseLeave}
      menuIcon={menuIcon}
      menuTriggerLabel={menuLabel}
      menuDisplayMode={menuDisplayMode}
      isDownloadEnabled={isDownloadEnabled}
      intents={intents}
      handleDownloadDataToolbar={handleDownloadDataToolbar}
      DownloadButton={() => <DownloadDataButton />}
    >
      <>
        <ExploreButton placement={exploreBtnPlace} />
        {isZoomEnabled && <ZoomModeButton />}
        {isPanEnabled && <PanButton />}
      </>
      {isZoomEnabled && <ZoomInOutButtons />}
      {isZoomEnabled && <ResetButton />}
      {showDefaultToolbarDownloadButton && <DownloadDataButton />}
    </ChartToolbarRenderer>
  ) : null;
};

XYChartToolbar['displayName'] = 'XYChartToolbar';
